(function () {
  'use strict';

  if (window.customElements.get('cart-recommendations')) return;

  var SIZE_SELECTOR = '.cart-recommendations__size';

  var CartRecommendations = class extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      if (typeof this._adding === 'undefined') this._adding = false;
      this._swiper = null;
      this._abortController = new AbortController();
      this._onClick = this._handleClick.bind(this);
      this._onKeydown = this._handleKeydown.bind(this);
      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeydown);
      this.hidden = true;
      this.querySelector('.swiper-wrapper').replaceChildren();
      var error = this.querySelector('.cart-recommendations__error');
      if (error) error.hidden = true;
      var link = this.querySelector('.cart-recommendations__cart-link');
      if (link) link.hidden = true;
      this._loadRecommendations(this._abortController.signal);
    }

    disconnectedCallback() {
      this._connected = false;
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('keydown', this._onKeydown);
      if (this._abortController) {
        this._abortController.abort();
        this._abortController = null;
      }
      if (this._swiper) {
        this._swiper.destroy(true, true);
        this._swiper = null;
      }
    }

    _loadRecommendations(signal) {
      var self = this;
      fetch(this.dataset.url, { credentials: 'same-origin', method: 'GET', signal: signal })
        .then(function (response) {
          if (!response.ok) throw new Error('Unable to load recommendations');
          return response.text();
        })
        .then(function (html) {
          if (signal.aborted || !self.isConnected) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var cards = doc.querySelectorAll('[data-cart-recommendation-results] .cart-recommendations__card');
          if (!cards.length) return;
          var wrapper = self.querySelector('.swiper-wrapper');
          if (!wrapper) return;
          var count = Math.min(cards.length, 5);
          for (var i = 0; i < count; i++) {
            wrapper.appendChild(document.importNode(cards[i], true));
          }
          wrapper.querySelectorAll('.cart-recommendations__sizes').forEach(function (sizes) {
            sizes.addEventListener('wheel', function (event) {
              event.stopPropagation();
            }, { passive: true });
          });
          if (signal.aborted || !self.isConnected) return;
          self.hidden = false;
          if (self._adding) self._setSizesDisabled(true);
          if (typeof Swiper === 'function') {
            self._swiper = new Swiper(self.querySelector('.cart-recommendations__slider'), {
              slidesPerView: 2.2,
              spaceBetween: 10,
              grabCursor: true,
              mousewheel: {
                enabled: true,
                forceToAxis: true,
                invert: true,
                releaseOnEdges: true
              },
              watchOverflow: true,
              observer: true,
              observeParents: true,
              scrollbar: {
                el: self.querySelector('.cart-recommendations__scrollbar'),
                draggable: true
              }
            });
          }
        })
        .catch(function () {});
    }

    _handleClick(event) {
      var toggle = event.target.closest('.cart-recommendations__toggle');
      if (toggle && this.contains(toggle)) {
        this._toggleSizes(toggle);
        return;
      }
      var size = event.target.closest(SIZE_SELECTOR);
      if (size && this.contains(size)) {
        this._addVariant(size);
      }
    }

    _handleKeydown(event) {
      if (event.key !== 'Escape') return;
      var openToggle = this.querySelector('.cart-recommendations__toggle[aria-expanded="true"]');
      if (!openToggle) return;
      event.preventDefault();
      event.stopPropagation();
      this._setSizes(openToggle, false);
      openToggle.focus();
    }

    _sizesFor(toggle) {
      return this.querySelector('#' + toggle.getAttribute('aria-controls'));
    }

    _setSizes(toggle, open) {
      var sizes = this._sizesFor(toggle);
      if (!sizes) return;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      sizes.hidden = !open;
    }

    _toggleSizes(toggle) {
      var open = toggle.getAttribute('aria-expanded') === 'true';
      var toggles = this.querySelectorAll('.cart-recommendations__toggle[aria-expanded="true"]');
      for (var i = 0; i < toggles.length; i++) {
        if (toggles[i] !== toggle) this._setSizes(toggles[i], false);
      }
      this._setSizes(toggle, !open);
    }

    _setSizesDisabled(disabled) {
      var buttons = this.querySelectorAll(SIZE_SELECTOR);
      for (var i = 0; i < buttons.length; i++) {
        if (disabled) {
          buttons[i].dataset.wasDisabled = buttons[i].disabled ? 'true' : 'false';
          buttons[i].disabled = true;
        } else {
          buttons[i].disabled = buttons[i].dataset.wasDisabled === 'true';
          delete buttons[i].dataset.wasDisabled;
        }
      }
    }

    _showError(message) {
      var error = this.querySelector('.cart-recommendations__error');
      if (error) {
        error.textContent = message;
        error.hidden = false;
      }
    }

    _addVariant(button) {
      var self = this;
      if (button.disabled || this._adding) return;
      this._adding = true;
      this.setAttribute('aria-busy', 'true');
      var error = this.querySelector('.cart-recommendations__error');
      if (error) {
        error.textContent = '';
        error.hidden = true;
      }
      this._setSizesDisabled(true);

      var card = button.closest('.cart-recommendations__card');
      var originalPrice = card ? card.dataset.originalPrice : null;
      var item = { id: Number(button.dataset.variantId), quantity: 1, properties: originalPrice ? { '_🔥Original Price': originalPrice } : {} };

      var added = false;
      fetch(this.dataset.addUrl, {
        credentials: 'same-origin',
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [item] })
      })
        .then(function (response) {
          if (!response.ok) {
            return response.json().catch(function () { return {}; }).then(function (data) {
              throw new Error(data.description || self.dataset.error || 'Unable to add item');
            });
          }
          added = true;
          return fetch(self.dataset.cartUrl + '.js', { credentials: 'same-origin', method: 'GET' });
        })
        .then(function (cartResponse) {
          if (!cartResponse.ok) throw new Error('Unable to load cart');
          return cartResponse.json();
        })
        .then(function (cart) {
          return window.ajaxCart.refresh(cart);
        })
        .catch(function (error) {
          if (!self.isConnected) return;
          self.removeAttribute('aria-busy');
          if (added) {
            self._onRefreshFailure();
            return;
          }
          self._adding = false;
          self._setSizesDisabled(false);
          self._showError(error && error.message ? error.message : self.dataset.error);
        });
    }

    _onRefreshFailure() {
      this._showError(this.dataset.refreshError || 'Added to cart');
      var link = this.querySelector('.cart-recommendations__cart-link');
      if (link) link.hidden = false;
    }
  };

  window.customElements.define('cart-recommendations', CartRecommendations);
})();
